import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import isPositiveInteger from 'validators/isPositiveInteger'
import { getCheckedReasonOptions, mapTriggerReasonToApi } from 'utils/criterionCHelpers'
import capitalize from 'utils/capitalize'

export const findHistoryId = (site, editOnlySiteStatus, params) =>
  editOnlySiteStatus ? (site.sitehistoryId || params.siteHistoryId) : params.siteHistoryId

export const findRawSiteId = (site, propSiteId, params) =>
  propSiteId || site?.id || site?.siteId || params?.siteId || params?.id

export const findSiteId = (rawSiteId) =>
  typeof rawSiteId === 'object' ? (rawSiteId?.id || rawSiteId?.siteId) : rawSiteId

export const getCriterionCEntityId = (criterionC) =>
  criterionC.site_ecointegrity_history_id || criterionC.site_ecointegrity_id

export const setDisabled = (site, qualified, isEditableStatus, isEditable) =>
  !isEditableStatus || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualified

export const setDisabledQualificationReason = (site, isEditableStatus, isEditable) =>
  !isEditableStatus || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)

export const setShowReassessmentFields = (criterionC, site, defaultPage) =>
  criterionC.last_reassessment_date !== null && site.reassessmentFlag && !defaultPage

export const setShowFinishButton = (site, defaultPage) =>
  site.SiteStatusId === SITE_STATUS_DRAFT_ID && !defaultPage

export const hasTriggerReasons = (triggerReasonIds) => Array.isArray(triggerReasonIds) && triggerReasonIds.length > 0

export const getSelectedSite = (criterionC) => {
  if (criterionC.EcoRegion?.id) {
    return {
      label: criterionC.EcoRegion.name,
      value: criterionC.EcoRegion.id
    }
  }
  return null
}

export const toEcoRegionOption = (element) => ({
  value: element.id,
  label: capitalize(element.name)
})

export const getEcoRegionOptions = async (query, requestEcoRegionsSearch) => {
  const data = await requestEcoRegionsSearch(query)
  return data.map(toEcoRegionOption)
}

export const resolveCriterionCCheckFields = (siteProposalFieldValidation, criterionC, defaultCriterionCValidation) =>
  siteProposalFieldValidation?.criterionC?.[criterionC.site_ecointegrity_id || criterionC.site_ecointegrity_history_id] ||
  defaultCriterionCValidation

export const normalizeYearInput = (value, minValidYear, yearDigitCount) => {
  const currentYear = new Date().getFullYear()
  const canDisplay = value <= currentYear && !value.includes('.') && !value.includes(' ') && (value > 0 || value === '')
  const canPersist = value <= currentYear && value > minValidYear && value.length === yearDigitCount
  return { canDisplay, canPersist, parsed: Number.parseInt(value, 10) || null }
}

export const fetchCriterionCFilesByScope = async ({ siteId, historyId, isReassessment, requestGetCriterionCFiles }) => {
  if (!siteId) return { files: [] }
  return isReassessment ? requestGetCriterionCFiles(siteId, historyId) : requestGetCriterionCFiles(siteId)
}

export const createCriterionCHandlers = ({
  persistCriterionCField,
  setEcoRegionQuery,
  setEcoRegionOptions,
  setEcoRegion,
  setEcoRegionSite,
  setEcoIntegrity,
  setEcoIntegrity2,
  setYear,
  setQualificationReason,
  setReassessmentRational,
  setCheckedOptions,
  checkedValue,
  updateEcoRegionQueryTimeOut,
  updateEcologicalIntegrityEcoRegionTimeOut,
  updateNumberOfCriterionSitesInEcoRegionTimeOut,
  updateEvidenceOfLowHumanImpactTimeOut,
  updateEvidenceForIntactEcologicalCommunitiesTimeOut,
  updateDateOfAssesmentTimeOut,
  updateTriggerReasonIdTimeOut,
  updateTriggerReasonTextTimeOut,
  getEcoRegionOptions,
  minValidYear,
  yearDigitCount
}) => ({
  handleChangeValueChecked: async (Ids) => {
    checkedValue.current = Ids
    setCheckedOptions(getCheckedReasonOptions(Ids))
  },
  handleChangeEcoRegionQuery: (e) => {
    setEcoRegionQuery(e)
    if (e === '') return
    updateEcoRegionQueryTimeOut(() => {
      getEcoRegionOptions(e).then(setEcoRegionOptions)
    })
  },
  handleChangeEcoRegion: async (e) => {
    setEcoRegion(e)
    await persistCriterionCField({ ecoregion_id: e.value }, updateEcologicalIntegrityEcoRegionTimeOut)
  },
  handleChangeEcoRegionSite: async (e) => {
    if (/^[0-9 ]*$/.test(e.target.value) || e.target.value === '') {
      setEcoRegionSite(e.target.value)
    }
    const numberOfCriterionSitesInEcoRegion = e.target.value ? Number(e.target.value) : null
    if (isPositiveInteger(e.target.value) || e.target.value === '') {
      await persistCriterionCField(
        { ecoregion_c_sites_count: numberOfCriterionSitesInEcoRegion },
        updateNumberOfCriterionSitesInEcoRegionTimeOut
      )
    }
  },
  handleChangeEcoIntegrity: async (e) => {
    setEcoIntegrity(e.target.value)
    await persistCriterionCField({ evidence_low_human_impact: e.target.value }, updateEvidenceOfLowHumanImpactTimeOut)
  },
  handleChangeEcoIntegrity2: async (e) => {
    setEcoIntegrity2(e.target.value)
    await persistCriterionCField({ evidence_intact_ecology: e.target.value }, updateEvidenceForIntactEcologicalCommunitiesTimeOut)
  },
  handleChangeYear: async (e) => {
    const passedValue = e.target.value
    const { canDisplay, canPersist, parsed } = normalizeYearInput(passedValue, minValidYear, yearDigitCount)
    if (canDisplay) {
      setYear(passedValue)
    }
    if (canPersist) {
      await persistCriterionCField({ year: parsed }, updateDateOfAssesmentTimeOut)
    }
  },
  handleChangeQualificationReason: async (e) => {
    setQualificationReason(e)
    const formattedReasons = mapTriggerReasonToApi(e)
    const triggerReasonParams = formattedReasons.includes(1) ? { trigger_reason_id: formattedReasons } : { trigger_reason_id: formattedReasons, ecoregion_id: null }
    await persistCriterionCField({ ...triggerReasonParams }, updateTriggerReasonIdTimeOut)
    if (formattedReasons.some(num => num > 1)) {
      setEcoRegion(null)
    }
  },
  handleChangeReassessmentRational: async (e) => {
    setReassessmentRational(e.target.value)
    await persistCriterionCField({ trigger_reason_text: e.target.value }, updateTriggerReasonTextTimeOut)
  }
})

export const createCriterionCUploaderHandlers = ({
  rawSiteId,
  historyId,
  isReassessment,
  requestGetCriterionCFiles,
  requestUpdateCriterionCFiles,
  requestDeleteCriterionCFiles,
  extractFiles,
  normFile,
  uniqByKey,
  setUploadedFiles
}) => {
  const refreshFilesFromResponse = async (response) => {
    let nextFiles = extractFiles(response).map(normFile)
    if (!nextFiles.length) {
      const res = await fetchCriterionCFilesByScope({
        siteId: rawSiteId,
        historyId,
        isReassessment,
        requestGetCriterionCFiles
      })
      nextFiles = extractFiles(res).map(normFile)
    }
    setUploadedFiles(uniqByKey(nextFiles))
  }

  const fetchUploaderFiles = async () => {
    try {
      return await fetchCriterionCFilesByScope({
        siteId: rawSiteId,
        historyId,
        isReassessment,
        requestGetCriterionCFiles
      })
    } catch (error) {
      console.error('Failed to fetch criterion C files:', error)
      return { files: [] }
    }
  }

  const uploadCriterionCFile = async (uploadSiteId, uploadSiteHistoryId, formData, options) => {
    const response = await requestUpdateCriterionCFiles(rawSiteId, historyId, formData, options)
    await refreshFilesFromResponse(response)
    return response
  }

  const deleteCriterionCFile = async (deleteSiteId, deleteSiteHistoryId, fileId) => {
    const response = await requestDeleteCriterionCFiles(rawSiteId, historyId, fileId)
    await refreshFilesFromResponse(response)
    return response
  }

  return { fetchUploaderFiles, uploadCriterionCFile, deleteCriterionCFile }
}
